import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CopperButton } from '../components/ui/copper-button';
import { SlateButton } from '../components/ui/slate-button';
import { GlassButton } from '../components/ui/glass-button';
import { GhostButton } from '../components/ui/ghost-button';
import { GlassInput } from '../components/ui/glass-input';
import { StatusPill } from '../components/ui/status-pill';
import { PersonaIcon } from '../components/ui/persona-icon';
import { ArrowLeft, Send, Check, X } from 'lucide-react';
import { toast } from 'sonner';

export const DesignTest = () => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');

  const showToast = (type) => {
    switch (type) {
      case 'success':
        toast.success('Success', { description: 'Operation completed successfully' });
        break;
      case 'info':
        toast('Information', { description: 'Here is some information for you' });
        break;
      case 'warning':
        toast.warning('Warning', { description: 'Please check this warning' });
        break;
      case 'error':
        toast.error('Error', { description: 'Something went wrong' });
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen px-6 py-12">
      <div className="container-app max-w-6xl">
        <GhostButton onClick={() => navigate('/')} className="mb-8">
          <ArrowLeft size={16} strokeWidth={1.75} />
          Back
        </GhostButton>

        <h1 className="text-heading-1 mb-3" style={{ color: 'var(--color-text-primary)' }}>
          FORA Design System
        </h1>
        <p className="text-body mb-12" style={{ color: 'var(--color-text-tertiary)' }}>
          Foundation showcase - Typography, colors, components, and interactions
        </p>

        {/* Typography Section */}
        <section className="mb-16">
          <h2 className="text-heading-2 mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Typography Scale
          </h2>
          <div className="glass-primary rounded-[var(--radius-xl)] p-8 space-y-6">
            <div>
              <p className="text-overline mb-2">Heading 1 - Sora 700</p>
              <h1 className="text-heading-1" style={{ color: 'var(--color-text-primary)' }}>
                The quick brown fox
              </h1>
            </div>
            <div>
              <p className="text-overline mb-2">Heading 2 - Sora 600</p>
              <h2 className="text-heading-2" style={{ color: 'var(--color-text-primary)' }}>
                The quick brown fox jumps over the lazy dog
              </h2>
            </div>
            <div>
              <p className="text-overline mb-2">Body - Inter 400</p>
              <p className="text-body" style={{ color: 'var(--color-text-secondary)' }}>
                The quick brown fox jumps over the lazy dog. Lorem ipsum dolor sit amet, consectetur adipiscing elit.
              </p>
            </div>
            <div>
              <p className="text-overline mb-2">Monospaced Numbers - DM Mono</p>
              <div className="text-mono text-2xl tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                ₹1,234.56 • Q-007 • 00:04:32
              </div>
            </div>
          </div>
        </section>

        {/* Button Variants Section */}
        <section className="mb-16">
          <h2 className="text-heading-2 mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Button System
          </h2>
          <div className="glass-primary rounded-[var(--radius-xl)] p-8">
            <div className="space-y-8">
              <div>
                <p className="text-overline mb-4">Primary Copper Button (with metallic sheen)</p>
                <div className="flex flex-wrap gap-4">
                  <CopperButton size="sm">Small</CopperButton>
                  <CopperButton size="md">
                    <Send size={18} strokeWidth={1.75} />
                    Medium
                  </CopperButton>
                  <CopperButton size="lg">Large</CopperButton>
                  <CopperButton size="xl">Extra Large</CopperButton>
                  <CopperButton disabled>Disabled</CopperButton>
                </div>
              </div>
              
              <div>
                <p className="text-overline mb-4">Secondary Slate Button</p>
                <div className="flex flex-wrap gap-4">
                  <SlateButton size="md">Slate Button</SlateButton>
                  <SlateButton size="lg">
                    <Check size={18} strokeWidth={1.75} />
                    With Icon
                  </SlateButton>
                  <SlateButton disabled>Disabled</SlateButton>
                </div>
              </div>

              <div>
                <p className="text-overline mb-4">Tertiary Glass Button</p>
                <div className="flex flex-wrap gap-4">
                  <GlassButton size="md">Glass Button</GlassButton>
                  <GlassButton size="lg">
                    <X size={18} strokeWidth={1.75} />
                    With Icon
                  </GlassButton>
                  <GlassButton disabled>Disabled</GlassButton>
                </div>
              </div>

              <div>
                <p className="text-overline mb-4">Ghost Button (Text-Link Style)</p>
                <div className="flex flex-wrap gap-4">
                  <GhostButton size="sm">Small</GhostButton>
                  <GhostButton size="md">Medium</GhostButton>
                  <GhostButton size="lg">Large</GhostButton>
                  <GhostButton disabled>Disabled</GhostButton>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Input System Section */}
        <section className="mb-16">
          <h2 className="text-heading-2 mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Input System
          </h2>
          <div className="glass-primary rounded-[var(--radius-xl)] p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <GlassInput
                label="Email Address"
                type="email"
                placeholder="your@email.com"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <GlassInput
                label="With Error"
                type="text"
                placeholder="Enter something"
                error="This field is required"
              />
              <GlassInput
                label="Phone Number"
                type="tel"
                placeholder="+91 98765 43210"
              />
              <GlassInput
                label="Amount"
                type="number"
                placeholder="0.00"
              />
            </div>
          </div>
        </section>

        {/* Status Pills Section */}
        <section className="mb-16">
          <h2 className="text-heading-2 mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Status Pill System
          </h2>
          <div className="glass-primary rounded-[var(--radius-xl)] p-8">
            <div className="flex flex-wrap gap-4">
              <StatusPill status="in-queue" />
              <StatusPill status="waiting" />
              <StatusPill status="next-in-line" />
              <StatusPill status="in-consultation" />
              <StatusPill status="payment-pending" />
              <StatusPill status="completed" />
              <StatusPill status="no-show" />
            </div>
          </div>
        </section>

        {/* Persona Icons Section */}
        <section className="mb-16">
          <h2 className="text-heading-2 mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Persona Icon System
          </h2>
          <div className="glass-primary rounded-[var(--radius-xl)] p-8">
            <div className="grid grid-cols-5 gap-8">
              <PersonaIcon persona="patient" size="lg" showLabel />
              <PersonaIcon persona="receptionist" size="lg" showLabel />
              <PersonaIcon persona="doctor" size="lg" showLabel />
              <PersonaIcon persona="billing" size="lg" showLabel />
              <PersonaIcon persona="owner" size="lg" showLabel />
            </div>
          </div>
        </section>

        {/* Glass Surface Variants Section */}
        <section className="mb-16">
          <h2 className="text-heading-2 mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Glass Surface Variants
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="glass-primary rounded-[var(--radius-xl)] p-6">
              <p className="text-overline mb-2">Glass Primary</p>
              <p className="text-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Main cards, sidebars, panels. 62% opacity, 24px blur.
              </p>
            </div>
            <div className="glass-elevated rounded-[var(--radius-xl)] p-6">
              <p className="text-overline mb-2">Glass Elevated</p>
              <p className="text-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Modals, popovers. 78% opacity, 32px blur.
              </p>
            </div>
            <div className="glass-subtle rounded-[var(--radius-xl)] p-6">
              <p className="text-overline mb-2">Glass Subtle</p>
              <p className="text-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Inputs, pills, chips. 50% opacity, 16px blur.
              </p>
            </div>
          </div>
        </section>

        {/* Toast Notifications Section */}
        <section className="mb-16">
          <h2 className="text-heading-2 mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Toast Notification System
          </h2>
          <div className="glass-primary rounded-[var(--radius-xl)] p-8">
            <div className="flex flex-wrap gap-4">
              <CopperButton size="md" onClick={() => showToast('success')}>
                <Check size={18} strokeWidth={1.75} />
                Success Toast
              </CopperButton>
              <SlateButton size="md" onClick={() => showToast('info')}>
                Info Toast
              </SlateButton>
              <GlassButton size="md" onClick={() => showToast('warning')}>
                Warning Toast
              </GlassButton>
              <GhostButton size="md" onClick={() => showToast('error')}>
                Error Toast
              </GhostButton>
            </div>
          </div>
        </section>

        {/* Color Palette Section */}
        <section className="mb-16">
          <h2 className="text-heading-2 mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Color System
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="glass-primary rounded-[var(--radius-xl)] p-6">
              <p className="text-overline mb-4">Warm Text Hierarchy</p>
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg" style={{ backgroundColor: '#1C1917' }}></div>
                  <div>
                    <p className="text-label" style={{ color: 'var(--color-text-primary)' }}>Primary</p>
                    <p className="text-xs text-mono" style={{ color: 'var(--color-text-muted)' }}>#1C1917</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg" style={{ backgroundColor: '#44403C' }}></div>
                  <div>
                    <p className="text-label" style={{ color: 'var(--color-text-primary)' }}>Secondary</p>
                    <p className="text-xs text-mono" style={{ color: 'var(--color-text-muted)' }}>#44403C</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg" style={{ backgroundColor: '#78716C' }}></div>
                  <div>
                    <p className="text-label" style={{ color: 'var(--color-text-primary)' }}>Tertiary</p>
                    <p className="text-xs text-mono" style={{ color: 'var(--color-text-muted)' }}>#78716C</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-primary rounded-[var(--radius-xl)] p-6">
              <p className="text-overline mb-4">Metallic Copper Gradient</p>
              <div className="space-y-3">
                <div className="h-16 rounded-lg bg-copper-gradient relative overflow-hidden">
                  <span className="metallic-highlight absolute inset-0"></span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <div className="w-full h-12 rounded-lg" style={{ backgroundColor: '#E89B6F' }}></div>
                    <p className="text-xs text-mono mt-1" style={{ color: 'var(--color-text-muted)' }}>Light</p>
                  </div>
                  <div>
                    <div className="w-full h-12 rounded-lg" style={{ backgroundColor: '#D4784A' }}></div>
                    <p className="text-xs text-mono mt-1" style={{ color: 'var(--color-text-muted)' }}>Mid</p>
                  </div>
                  <div>
                    <div className="w-full h-12 rounded-lg" style={{ backgroundColor: '#B5612E' }}></div>
                    <p className="text-xs text-mono mt-1" style={{ color: 'var(--color-text-muted)' }}>Dark</p>
                  </div>
                  <div>
                    <div className="w-full h-12 rounded-lg" style={{ backgroundColor: '#8E4A23' }}></div>
                    <p className="text-xs text-mono mt-1" style={{ color: 'var(--color-text-muted)' }}>Deeper</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Animation Showcase */}
        <section className="mb-16">
          <h2 className="text-heading-2 mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Animation System
          </h2>
          <div className="glass-primary rounded-[var(--radius-xl)] p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="glass-subtle rounded-[var(--radius-lg)] p-6 animate-fade-in">
                <p className="text-label mb-2" style={{ color: 'var(--color-text-primary)' }}>Fade In</p>
                <p className="text-body-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                  0.2s ease-out
                </p>
              </div>
              <div className="glass-subtle rounded-[var(--radius-lg)] p-6 animate-slide-up">
                <p className="text-label mb-2" style={{ color: 'var(--color-text-primary)' }}>Slide Up</p>
                <p className="text-body-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                  0.24s ease-out
                </p>
              </div>
              <div className="glass-subtle rounded-[var(--radius-lg)] p-6 hover-lift interactive">
                <p className="text-label mb-2" style={{ color: 'var(--color-text-primary)' }}>Hover Lift</p>
                <p className="text-body-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                  Hover to see effect
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DesignTest;
